// New hire intake (2026-10-16). A foreman enters a new hire's details from his
// phone; the office (owner / road_super / admin) reviews and approves. The
// approved intake pre-fills the DocuSign new-hire packet.
//
// A FOREMAN SEES ONLY THE FORM (Patrick, 2026-10-16) — never the list of
// intakes. After sending he gets a confirmation and "Enter another". There is
// NO pay rate anywhere on this screen.
// Every rule and every role decision lives in api/new-hires — this screen only
// renders what the server sends and shows the server's field errors.
//
// NO SSN NUMBER on this screen, ever — the new hire types it into DocuSign.
// 2026-10-17 (Patrick): the form now takes the date of birth, photos of the
// documents the new hire chose for the I-9, and (on a second page) his safety
// certifications with a photo of each. Only owners and Carla can open ID photos;
// they are deleted when Carla marks the I-9 done.
//
// 2026-10-24: the SAME form also runs on the new hire's own phone (NewHireSelfPage,
// /new-hire/start?t=) in English or Spanish — `self` = { api, lang, invite }. There
// the job / union / trade / start date are read-only (the sender set them), and every
// call goes to the token-only public routes. The office/foreman can also SEND the form
// to the new hire by text and/or email (SendInviteForm below).
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { newHiresGet, newHiresPost, uploadNewHireFile, discardNewHireUpload, downloadNewHirePacketDoc } from '../lib/db'

const STAFF_API = { get: newHiresGet, post: newHiresPost, upload: uploadNewHireFile, discard: discardNewHireUpload }

const EMPTY = {
  first_name: '', middle_initial: '', last_name: '',
  street: '', apt: '', city: '', state: 'NJ', zip: '',
  cell_phone: '', email: '', emergency_name: '', emergency_phone: '',
  start_date: '', job_id: '', union_local: '', trade_no: '', date_of_birth: '',
  employee_type: 'field',
}

// "589 Ivoclar", or "Office" for an office hire with no starting job.
const startsAt = (i) => [i.job_number, i.job_name].filter(Boolean).join(' ')

function ageOn(dob, today) {
  if (!/^\d{4}-\d{2}-\d{2}/.test(String(dob || '')) || !/^\d{4}-\d{2}-\d{2}/.test(String(today || ''))) return null
  const [by, bm, bd] = String(dob).slice(0, 10).split('-').map(Number)
  const [ty, tm, td] = String(today).slice(0, 10).split('-').map(Number)
  let a = ty - by
  if (tm < bm || (tm === bm && td < bd)) a--
  return a
}

// Open a 5-minute link to one stored photo. The tab is opened BEFORE the request so
// phone browsers don't block it as a pop-up.
async function openPhoto(id, kind, n, onErr) {
  const w = window.open('', '_blank')
  const r = await newHiresGet('file', { id, kind, n })
  if (r.error || !r.url) {
    if (w) w.close()
    onErr(r.detail || r.error || 'Could not open the photo')
    return
  }
  if (w) w.location.href = r.url
  else window.location.href = r.url
}

// Take a photo (camera opens directly, so the picture is not left in the phone's
// gallery) or choose a file. Uploads straight away; shows each file with a remove x.
function PhotoButtons({ onFile, disabled, compact, lang }) {
  const es = lang === 'es'
  const cam = useRef(null)
  const pick = useRef(null)
  const take = (e) => { const f = e.target.files && e.target.files[0]; e.target.value = ''; if (f) onFile(f) }
  return (
    <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
      <input ref={cam} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={take} />
      <input ref={pick} type="file" accept="image/*,application/pdf" style={{ display: 'none' }} onChange={take} />
      <button type="button" className={compact ? 'btn btn-sm' : 'btn'} disabled={disabled} onClick={() => cam.current && cam.current.click()}>{es ? 'Tomar foto' : 'Take photo'}</button>
      <button type="button" className={compact ? 'btn btn-sm' : 'btn'} disabled={disabled} onClick={() => pick.current && pick.current.click()}>{es ? 'Elegir archivo' : 'Choose file'}</button>
    </span>
  )
}

const uploadErr = (r) => r.detail || (r.error === 'not_signed_in' ? 'You are signed out - sign in again' : r.error) || 'Upload failed'

const STATUS = {
  invited: { label: 'Invited - waiting on new hire', bg: '#F1EFE8', fg: '#5F5E5A' },
  submitted: { label: 'Waiting for office', bg: '#FAEEDA', fg: '#854F0B' },
  approved: { label: 'Approved - DocuSign next', bg: '#E6F1FB', fg: '#0C447C' },
  sent: { label: 'DocuSign sent', bg: '#EAF3DE', fg: '#3B6D11' },
  void: { label: 'Void', bg: '#EEE', fg: '#666' },
}

function fmtPhone(p) {
  const d = String(p || '').replace(/\D/g, '')
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : String(p || '')
}
function fmtDate(ymd) {
  if (!/^\d{4}-\d{2}-\d{2}/.test(String(ymd || ''))) return ''
  const [y, m, d] = String(ymd).slice(0, 10).split('-')
  return `${Number(m)}/${Number(d)}/${y}`
}
function fmtWhen(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleString('en-US', { month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}
const nameOf = (i) => [i.first_name, i.middle_initial ? i.middle_initial + '.' : '', i.last_name].filter(Boolean).join(' ')
const newSession = () => (window.crypto && window.crypto.randomUUID ? window.crypto.randomUUID() : String(Date.now()) + Math.random())

function Chip({ status }) {
  const s = STATUS[status] || STATUS.void
  return <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: s.bg, color: s.fg, fontWeight: 500, whiteSpace: 'nowrap' }}>{s.label}</span>
}

function Field({ label, error, children, hint }) {
  return (
    <div className="form-group" style={{ marginBottom: 10 }}>
      <label className="form-label">{label}</label>
      {children}
      {hint && !error && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3 }}>{hint}</div>}
      {error && <div style={{ fontSize: 12, color: '#A32D2D', marginTop: 3 }}>{error}</div>}
    </div>
  )
}

// ── The intake form (create, or edit an existing intake) ─────────────────────
export function IntakeForm({ options, initial, onDone, onCancel, isMobile, self }) {
  const api = self ? self.api : STAFF_API
  const lang = self ? self.lang : 'en'
  const L = (en, es) => (lang === 'es' ? es : en)
  const [f, setF] = useState(() => initial ? { ...EMPTY, ...pickForm(initial) }
    : self ? { ...EMPTY, ...pickForm(self.invite, ['first_name', 'middle_initial', 'last_name', 'cell_phone', 'email', 'employee_type', 'union_local']) }
    : { ...EMPTY })
  const [errors, setErrors] = useState({})
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState('')
  const [sugs, setSugs] = useState([])
  const [showSugs, setShowSugs] = useState(false)
  const session = useRef(newSession())
  const timer = useRef(null)
  // Photos + certifications ride with a NEW intake only (the office edits details).
  const creating = !initial
  const certTypes = (creating && options.cert_types) || []
  const [step, setStep] = useState(1)                 // 1 = details + ID, 2 = certifications
  const [idDocs, setIdDocs] = useState([])            // [{ path, name, size, mime }]
  const [certs, setCerts] = useState({})              // cert_type_id -> { file|null }
  const [uploading, setUploading] = useState(0)
  const [photoErr, setPhotoErr] = useState('')

  const addIdDoc = async (file) => {
    setPhotoErr(''); setUploading(n => n + 1)
    const r = await api.upload('id', file)
    setUploading(n => n - 1)
    if (r.error) { setPhotoErr(uploadErr(r)); return }
    setIdDocs(prev => [...prev, r])
    if (errors.id_docs) setErrors(prev => ({ ...prev, id_docs: undefined }))
  }
  const removeIdDoc = (i) => {
    setIdDocs(prev => { api.discard(prev[i] && prev[i].path); return prev.filter((_, k) => k !== i) })
  }
  const toggleCert = (id) => setCerts(prev => {
    const next = { ...prev }
    if (next[id]) { api.discard(next[id].file && next[id].file.path); delete next[id] } else next[id] = { file: null }
    return next
  })
  const addCertPhoto = async (id, file) => {
    setPhotoErr(''); setUploading(n => n + 1)
    const r = await api.upload('cert', file)
    setUploading(n => n - 1)
    if (r.error) { setPhotoErr(uploadErr(r)); return }
    setCerts(prev => {
      if (prev[id] && prev[id].file) api.discard(prev[id].file.path)
      return { ...prev, [id]: { file: r } }
    })
  }
  const set = (k, v) => { setF(prev => ({ ...prev, [k]: v })); if (errors[k]) setErrors(prev => ({ ...prev, [k]: undefined })) }
  // Trade is a SUBCATEGORY of the union: its dropdown lists only the picked union's
  // trades (Foundation union -> trade list, served by the API). Changing the union
  // clears a trade that doesn't belong to the new one.
  const tradeOptions = ((options.union_trades || []).find(g => g.union === f.union_local) || { trades: [] }).trades
  // Office hire (2026-10-22): no union, no trade, starting job optional.
  const office = f.employee_type === 'office'
  const pickType = (k) => {
    setF(prev => ({ ...prev, employee_type: k, ...(k === 'office' ? { union_local: '', trade_no: '' } : {}) }))
    setErrors(prev => ({ ...prev, employee_type: undefined, union_local: undefined, trade_no: undefined, job_id: undefined }))
  }
  const pickUnion = (v) => {
    const keep = ((options.union_trades || []).find(g => g.union === v) || { trades: [] }).trades.some(t => t.trade_no === f.trade_no)
    setF(prev => ({ ...prev, union_local: v, trade_no: keep ? prev.trade_no : '' }))
    setErrors(prev => ({ ...prev, union_local: undefined, trade_no: undefined }))
  }

  const onStreet = (v) => {
    set('street', v)
    if (!options.address_lookup) return
    clearTimeout(timer.current)
    if (v.trim().length < 4) { setSugs([]); return }
    timer.current = setTimeout(async () => {
      const r = await api.get('address-suggest', { q: v, session: session.current })
      setSugs(r.suggestions || [])
      setShowSugs(true)
    }, 300)
  }
  const pickSug = async (s) => {
    setShowSugs(false); setSugs([])
    const r = await api.get('address-detail', { place_id: s.place_id, session: session.current })
    session.current = newSession() // a pick ends the billing session
    if (r.address) {
      setF(prev => ({
        ...prev,
        street: r.address.street || s.main || prev.street,
        apt: r.address.apt || prev.apt,
        city: r.address.city || prev.city,
        state: r.address.state || prev.state,
        zip: r.address.zip || prev.zip,
      }))
      setErrors(prev => ({ ...prev, street: undefined, city: undefined, state: undefined, zip: undefined }))
    } else {
      set('street', s.main)
    }
  }

  const PAGE2 = new Set(['certs'])
  const submit = async () => {
    setBusy(true); setMsg('')
    const body = initial ? { ...f, id: initial.id } : {
      ...f,
      id_docs: idDocs,
      certs: Object.entries(certs).map(([id, v]) => ({ cert_type_id: Number(id), file: v.file || null })),
    }
    const r = self ? await api.post('submit', body) : initial ? await api.post('update', body) : await api.post('create', body)
    setBusy(false)
    if (r.error) {
      if (r.errors) {
        setErrors(r.errors)
        const keys = Object.keys(r.errors).filter(k => r.errors[k])
        if (keys.some(k => !PAGE2.has(k))) setStep(1)
        setMsg(L('Fix the highlighted fields.', 'Corrija los campos marcados.'))
      } else setMsg(r.detail || r.error)
      return
    }
    onDone(self ? r : r.intake)
  }
  const next = () => { setMsg(''); setStep(2); window.scrollTo && window.scrollTo(0, 0) }

  const two = { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0 12px' }
  const inp = (k, props = {}) => (
    <input className="form-input" value={f[k]} onChange={e => set(k, e.target.value)}
      style={errors[k] ? { borderColor: '#A32D2D' } : undefined} {...props} />
  )

  return (
    <div className="card" style={{ padding: 14 }}>
      <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4 }}>{initial ? `Edit ${nameOf(initial)}` : self ? L('Your new hire form', 'Su formulario de nuevo empleado') : START.enter.title}</div>
      <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 12 }}>
        {self
          ? (step === 1
            ? L('Fill in your information. The office checks it, then you get the DocuSign paperwork (W-4 and the rest) with this already filled in. Do NOT type your Social Security number here - you enter it in DocuSign.',
              'Llene su información. La oficina la revisa y después usted recibe los documentos de DocuSign (el W-4 y los demás) con esto ya llenado. NO escriba su número de Seguro Social aquí - lo escribe en DocuSign.')
            : L('Step 2 of 2: check every safety certification you have, and take a photo of each card.',
              'Paso 2 de 2: marque cada certificación de seguridad que tenga y tome una foto de cada tarjeta.'))
          : step === 1
          ? <>Fill in what you know. The office checks it, then the new hire gets the DocuSign paperwork with this already filled in.
            Do NOT type their Social Security number here - they enter it in DocuSign.</>
          : <>Step 2 of 2: check every safety certification {f.first_name || 'the new hire'} has, and take a photo of each card.</>}
      </div>

      {step === 2 && (
        <div>
          {certTypes.map(t => {
            const on = !!certs[t.id]
            const file = on && certs[t.id].file
            return (
              <div key={t.id} style={{ padding: '8px 0', borderBottom: '0.5px solid var(--border)' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, cursor: 'pointer' }}>
                  <input type="checkbox" checked={on} onChange={() => toggleCert(t.id)} style={{ width: 20, height: 20 }} />
                  <span>{t.name}</span>
                </label>
                {on && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', margin: '6px 0 0 30px' }}>
                    {file
                      ? <span style={{ fontSize: 12, color: '#3B6D11' }}>{L('Photo added', 'Foto agregada')}</span>
                      : <span style={{ fontSize: 12, color: 'var(--text3)' }}>{L('Add a photo of the card:', 'Agregue una foto de la tarjeta:')}</span>}
                    <PhotoButtons compact lang={lang} disabled={busy} onFile={(fl) => addCertPhoto(t.id, fl)} />
                  </div>
                )}
              </div>
            )
          })}
          {errors.certs && <div style={{ fontSize: 12, color: '#A32D2D', marginTop: 6 }}>{errors.certs}</div>}
          {photoErr && <div style={{ fontSize: 12, color: '#A32D2D', marginTop: 6 }}>{photoErr}</div>}
          {msg && <div style={{ fontSize: 13, color: '#A32D2D', margin: '8px 0 0' }}>{msg}</div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="btn" disabled={busy} onClick={() => setStep(1)}>{L('Back', 'Atrás')}</button>
            <button className="btn btn-primary" disabled={busy || uploading > 0} onClick={submit} style={isMobile ? { flex: 1, padding: '10px 0' } : undefined}>
              {busy ? L('Saving...', 'Enviando...') : uploading > 0 ? L('Uploading photo...', 'Subiendo foto...') : L('Send to office', 'Enviar a la oficina')}
            </button>
          </div>
        </div>
      )}

      <div style={step === 2 ? { display: 'none' } : undefined}>

      {self
        ? <>
          <SenderBlock invite={self.invite} lang={lang} />
          {self.invite.employee_type !== 'office' && <SelfUnionTrade invite={self.invite} f={f} setF={setF}
            errors={errors} setErrors={setErrors} set={set} L={L} />}
        </>
        : <Field label="Type of hire" error={errors.employee_type}
        hint={office ? 'Office staff are not in a union - no union local or trade needed.' : null}>
        <div style={{ display: 'flex', gap: 8 }}>
          {[['field', 'Field (union)'], ['office', 'Office']].map(([k, l]) => (
            <button key={k} type="button" className={'btn' + (f.employee_type === k ? ' btn-primary' : '')}
              onClick={() => pickType(k)} style={{ flex: 1 }}>{l}</button>
          ))}
        </div>
      </Field>}

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 70px' : '1fr 70px 1fr', gap: '0 10px' }}>
        <Field label={L('First name', 'Nombre')} error={errors.first_name}>{inp('first_name', { autoComplete: 'off' })}</Field>
        <Field label={L('M.I.', 'Inicial')} error={errors.middle_initial}>{inp('middle_initial', { maxLength: 2 })}</Field>
        {isMobile ? null : <Field label={L('Last name', 'Apellido')} error={errors.last_name}>{inp('last_name', { autoComplete: 'off' })}</Field>}
      </div>
      {isMobile && <Field label={L('Last name', 'Apellido')} error={errors.last_name}>{inp('last_name', { autoComplete: 'off' })}</Field>}

      <Field label={L('Street address', 'Dirección')} error={errors.street}
        hint={options.address_lookup ? L('Start typing and pick the address from the list', 'Empiece a escribir y elija la dirección de la lista') : null}>
        <div style={{ position: 'relative' }}>
          <input className="form-input" value={f.street} autoComplete="off"
            onChange={e => onStreet(e.target.value)} onBlur={() => setTimeout(() => setShowSugs(false), 200)}
            onFocus={() => sugs.length && setShowSugs(true)}
            style={errors.street ? { borderColor: '#A32D2D' } : undefined} />
          {showSugs && sugs.length > 0 && (
            <div style={{ position: 'absolute', zIndex: 20, left: 0, right: 0, top: '100%', background: '#fff', border: '1px solid var(--border2)', borderRadius: 8, boxShadow: '0 6px 18px rgba(0,0,0,0.12)', marginTop: 2 }}>
              {sugs.map(s => (
                <div key={s.place_id} onMouseDown={e => { e.preventDefault(); pickSug(s) }}
                  style={{ padding: '9px 12px', cursor: 'pointer', borderBottom: '0.5px solid var(--border)' }}>
                  <div style={{ fontSize: 13 }}>{s.main}</div>
                  <div style={{ fontSize: 11, color: 'var(--text3)' }}>{s.secondary}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Field>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '90px 1fr 70px 100px', gap: '0 10px' }}>
        <Field label={L('Apt', 'Apto')} error={errors.apt}>{inp('apt')}</Field>
        <Field label={L('City / town', 'Ciudad')} error={errors.city}>{inp('city')}</Field>
        <Field label={L('State', 'Estado')} error={errors.state}>{inp('state', { maxLength: 2, style: { textTransform: 'uppercase', ...(errors.state ? { borderColor: '#A32D2D' } : {}) } })}</Field>
        <Field label={L('ZIP', 'Código postal')} error={errors.zip}>{inp('zip', { inputMode: 'numeric', maxLength: 10 })}</Field>
      </div>

      <div style={two}>
        <Field label={L('Cell phone', 'Celular')} error={errors.cell_phone}>{inp('cell_phone', { type: 'tel', inputMode: 'tel' })}</Field>
        <Field label={L('Email (optional)', 'Correo electrónico (opcional)')} error={errors.email}
          hint={self
            ? L('The DocuSign paperwork comes to your cell phone by text. It also comes by email if you enter one.', 'Los documentos de DocuSign le llegan al celular por mensaje de texto. También por correo si escribe uno.')
            : 'The DocuSign paperwork goes to the cell phone by text. It also goes by email if one is entered.'}>{inp('email', { type: 'email', inputMode: 'email', autoCapitalize: 'none' })}</Field>
        <Field label={L('Emergency contact name', 'Nombre del contacto de emergencia')} error={errors.emergency_name}>{inp('emergency_name')}</Field>
        <Field label={L('Emergency contact phone', 'Teléfono del contacto de emergencia')} error={errors.emergency_phone}>{inp('emergency_phone', { type: 'tel', inputMode: 'tel' })}</Field>
      </div>

      {self && <Field label={L('Date of birth', 'Fecha de nacimiento')} error={errors.date_of_birth}>{inp('date_of_birth', { type: 'date', max: options.today })}</Field>}

      {!self && <div style={two}>
        <Field label="Start date" error={errors.start_date}>{inp('start_date', { type: 'date' })}</Field>
        <Field label={office ? 'Starting job (optional)' : 'Starting job'} error={errors.job_id}>
          <select className="form-select" value={f.job_id} onChange={e => set('job_id', e.target.value)}
            style={errors.job_id ? { borderColor: '#A32D2D' } : undefined}>
            <option value="">{office ? 'Office - Roselle (no job)' : 'Pick the job...'}</option>
            {options.jobs.map(j => <option key={j.id} value={j.id}>{j.num} {j.name}</option>)}
            {initial && initial.job_id && !options.jobs.some(j => String(j.id) === String(initial.job_id)) &&
              <option value={initial.job_id}>{initial.job_number} {initial.job_name} (inactive)</option>}
          </select>
        </Field>
        {!office && (
        <Field label="Union local" error={errors.union_local}>
          <select className="form-select" value={f.union_local} onChange={e => pickUnion(e.target.value)}
            style={errors.union_local ? { borderColor: '#A32D2D' } : undefined}>
            <option value="">Pick the local...</option>
            {options.union_locals.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
        </Field>
        )}
        {!office && (options.union_trades || []).length > 0 && (
          <Field label="Trade" error={errors.trade_no}>
            <select className="form-select" value={f.trade_no} onChange={e => set('trade_no', e.target.value)}
              disabled={!f.union_local || tradeOptions.length === 0}
              style={errors.trade_no ? { borderColor: '#A32D2D' } : undefined}>
              <option value="">{!f.union_local ? 'Pick the union first' : tradeOptions.length ? 'Pick the trade...' : 'No trades for this union'}</option>
              {tradeOptions.map(t => <option key={t.trade_no} value={t.trade_no}>{t.name}</option>)}
            </select>
          </Field>
        )}
        <Field label="Date of birth" error={errors.date_of_birth}>{inp('date_of_birth', { type: 'date', max: options.today })}</Field>
      </div>}

      {creating && (
        <div style={{ margin: '6px 0 12px', padding: 10, border: '0.5px solid var(--border2)', borderRadius: 8 }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>{L('ID for the I-9 (photos)', 'Identificación para el I-9 (fotos)')}</div>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 8 }}>
            {self
              ? L("You choose which ID to show - for example a passport, or a driver's license plus a Social Security card. Take a photo of each one. Only the owners and payroll can see these, and they are deleted once the I-9 is done.",
                'Usted elige qué identificación mostrar - por ejemplo un pasaporte, o una licencia de conducir más una tarjeta de Seguro Social. Tome una foto de cada una. Solo los dueños y la oficina de nómina pueden verlas, y se borran cuando se termina el I-9.')
              : <>Let the new hire choose which ID to show - for example a passport, <b>or</b> a driver's license plus a Social Security card.
            Take a photo of each one. Only the owners and Carla can see these, and they are deleted once the I-9 is done.</>}
          </div>
          {idDocs.map((d, i) => (
            <div key={d.path} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, padding: '3px 0' }}>
              <span style={{ color: '#3B6D11' }}>{L(`Photo ${i + 1} added`, `Foto ${i + 1} agregada`)}</span>
              <button type="button" className="btn btn-sm" disabled={busy} onClick={() => removeIdDoc(i)}>{L('Remove', 'Quitar')}</button>
            </div>
          ))}
          {idDocs.length < 4 && <PhotoButtons lang={lang} disabled={busy} onFile={addIdDoc} />}
          {uploading > 0 && <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 6 }}>{L('Uploading...', 'Subiendo...')}</div>}
          {errors.id_docs && <div style={{ fontSize: 12, color: '#A32D2D', marginTop: 6 }}>{errors.id_docs}</div>}
          {photoErr && step === 1 && <div style={{ fontSize: 12, color: '#A32D2D', marginTop: 6 }}>{photoErr}</div>}
        </div>
      )}

      {msg && <div style={{ fontSize: 13, color: '#A32D2D', margin: '4px 0 10px' }}>{msg}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        {creating && certTypes.length > 0
          ? <button className="btn btn-primary" disabled={busy || uploading > 0} onClick={next} style={isMobile ? { flex: 1, padding: '10px 0' } : undefined}>
              {uploading > 0 ? L('Uploading photo...', 'Subiendo foto...') : L('Next: certifications', 'Siguiente: certificaciones')}
            </button>
          : <button className="btn btn-primary" disabled={busy || uploading > 0} onClick={submit} style={isMobile ? { flex: 1, padding: '10px 0' } : undefined}>
              {busy ? L('Saving...', 'Enviando...') : uploading > 0 ? L('Uploading photo...', 'Subiendo foto...') : initial ? 'Save changes' : L('Send to office', 'Enviar a la oficina')}
            </button>}
        {onCancel && <button className="btn" disabled={busy} onClick={onCancel}>Cancel</button>}
      </div>
      </div>
    </div>
  )
}

function pickForm(i, keys = Object.keys(EMPTY)) {
  const o = {}
  for (const k of keys) o[k] = i[k] == null ? '' : String(i[k])
  return o
}

// What the sender set (job / union / trade / start date) — read-only on the new hire's page.
function SenderBlock({ invite, lang }) {
  const L = (en, es) => (lang === 'es' ? es : en)
  const office = invite.employee_type === 'office'
  const row = (k, v) => (
    <div style={{ display: 'flex', gap: 8, fontSize: 13, padding: '2px 0' }}>
      <span style={{ width: 130, flexShrink: 0, color: 'var(--text3)' }}>{k}</span><span style={{ flex: 1, minWidth: 0 }}>{v}</span>
    </div>
  )
  return (
    <div style={{ margin: '0 0 12px', padding: 10, background: 'var(--surface2)', borderRadius: 8 }}>
      {row(L('Start date', 'Fecha de inicio'), fmtDate(invite.start_date))}
      {row(L('Starting job', 'Trabajo'), invite.job_name || (office ? L('Office', 'Oficina') : ''))}
      {office && row(L('Type', 'Tipo'), L('Office (non-union)', 'Oficina (sin sindicato)'))}
      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>
        {L('Set by Cornerstone - ask your foreman or the office if any of this is wrong.', 'Lo puso Cornerstone - pregunte a su capataz o a la oficina si algo está mal.')}
      </div>
    </div>
  )
}

// Union + trade on the new hire's own page (2026-10-25). The union is always the new
// hire's to pick or correct (pre-selected with what the sender entered). The trade follows
// the union: read-only only while the sender set one AND the stored union is kept;
// otherwise it lists the picked union's trades and clears whenever the union changes.
// Option names stay Foundation's names. The server applies the same rules.
function SelfUnionTrade({ invite, f, setF, errors, setErrors, set, L }) {
  const trades = ((invite.union_trades || []).find(g => g.union === f.union_local) || { trades: [] }).trades
  const locked = invite.trade_locked && f.union_local === invite.union_local
  const pickUnion = (v) => {
    setF(prev => ({ ...prev, union_local: v, trade_no: '' }))
    setErrors(prev => ({ ...prev, union_local: undefined, trade_no: undefined }))
  }
  return (
    <>
      <Field label={L('Union local', 'Sindicato (Local)')} error={errors.union_local}>
        <select className="form-select" value={f.union_local} onChange={e => pickUnion(e.target.value)}
          style={errors.union_local ? { borderColor: '#A32D2D' } : undefined}>
          <option value="">{L('Pick your union', 'Elija su sindicato')}</option>
          {(invite.union_options || []).map(u => <option key={u} value={u}>{u}</option>)}
        </select>
      </Field>
      {locked
        ? <Field label={L('Trade', 'Oficio')}><div style={{ fontSize: 14, padding: '6px 0' }}>{invite.trade}</div></Field>
        : trades.length > 0 && (
          <Field label={L('Trade', 'Oficio')} error={errors.trade_no}>
            <select className="form-select" value={f.trade_no} onChange={e => set('trade_no', e.target.value)}
              style={errors.trade_no ? { borderColor: '#A32D2D' } : undefined}>
              <option value="">{L('Pick your trade...', 'Elija su oficio...')}</option>
              {trades.map(t => <option key={t.trade_no} value={t.trade_no}>{t.name}</option>)}
            </select>
          </Field>
        )}
    </>
  )
}

// What the invite / resend reply says about each channel, in words.
function deliveryLines(r, form) {
  const d = (r && r.delivered) || {}
  const out = []
  if (d.sms === true) out.push({ ok: true, text: `Text sent to ${fmtPhone(form.cell_phone)}` })
  else if (d.sms) out.push({ ok: false, text: `Text to ${fmtPhone(form.cell_phone)} did NOT send: ${d.sms}` })
  if (d.email === true) out.push({ ok: true, text: `Email sent to ${form.email}` })
  else if (d.email) out.push({ ok: false, text: `Email to ${form.email} did NOT send: ${d.email}` })
  return out
}

const INVITE_EMPTY = { first_name: '', last_name: '', cell_phone: '', email: '', employee_type: 'field', job_id: '', union_local: '', trade_no: '', start_date: '' }

// ── Send the form to the new hire (text / email, English / Spanish) ──────────
// Foremen use iPads with no texting, so the app sends it. What the sender enters
// here (job / union / trade / start date) is locked on the new hire's page.
function SendInviteForm({ options, onSent, onCancel, isMobile }) {
  const [f, setF] = useState({ ...INVITE_EMPTY })
  const smsOn = !!options.sms_available
  const [channels, setChannels] = useState({ sms: smsOn, email: !smsOn })
  const [language, setLanguage] = useState('en')
  const [consent, setConsent] = useState(false)
  const [errors, setErrors] = useState({})
  const [msg, setMsg] = useState('')
  const [busy, setBusy] = useState(false)
  const set = (k, v) => { setF(prev => ({ ...prev, [k]: v })); if (errors[k]) setErrors(prev => ({ ...prev, [k]: undefined })) }
  const office = f.employee_type === 'office'
  const tradeOptions = ((options.union_trades || []).find(g => g.union === f.union_local) || { trades: [] }).trades
  const inp = (k, props = {}) => (
    <input className="form-input" value={f[k]} onChange={e => set(k, e.target.value)}
      style={errors[k] ? { borderColor: '#A32D2D' } : undefined} {...props} />
  )
  const two = { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0 12px' }

  const send = async () => {
    setBusy(true); setMsg('')
    const ch = Object.keys(channels).filter(k => channels[k])
    const r = await newHiresPost('invite', { ...f, channels: ch, language, sms_consent: channels.sms ? consent : false })
    setBusy(false)
    if (r.error) {
      if (r.errors) { setErrors(r.errors); setMsg('Fix the highlighted fields.') } else setMsg(r.detail || r.error)
      return
    }
    onSent({ ...r, lines: deliveryLines(r, f), name: [f.first_name, f.last_name].filter(Boolean).join(' ') })
  }

  return (
    <div className="card" style={{ padding: 14 }}>
      <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4 }}>{START.invite.title}</div>
      <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 12 }}>
        The new hire gets a link and fills in the rest on their own device (address, date of birth, emergency contact, ID photos).
        The link works for 7 days. The job, union, trade and start date you pick here are locked on their form.
      </div>

      <Field label="Type of hire">
        <div style={{ display: 'flex', gap: 8 }}>
          {[['field', 'Field (union)'], ['office', 'Office']].map(([k, l]) => (
            <button key={k} type="button" className={'btn' + (f.employee_type === k ? ' btn-primary' : '')}
              onClick={() => { setF(prev => ({ ...prev, employee_type: k, ...(k === 'office' ? { union_local: '', trade_no: '' } : {}) })); setErrors({}) }} style={{ flex: 1 }}>{l}</button>
          ))}
        </div>
      </Field>
      <div style={two}>
        <Field label="First name" error={errors.first_name}>{inp('first_name', { autoComplete: 'off' })}</Field>
        <Field label="Last name" error={errors.last_name}>{inp('last_name', { autoComplete: 'off' })}</Field>
        <Field label={channels.sms ? 'Cell phone' : 'Cell phone (optional)'} error={errors.cell_phone}>{inp('cell_phone', { type: 'tel', inputMode: 'tel' })}</Field>
        <Field label={channels.email ? 'Email' : 'Email (optional)'} error={errors.email}>{inp('email', { type: 'email', inputMode: 'email', autoCapitalize: 'none' })}</Field>
      </div>

      <div style={two}>
        <Field label="Start date" error={errors.start_date}>{inp('start_date', { type: 'date' })}</Field>
        <Field label={office ? 'Starting job (optional)' : 'Starting job'} error={errors.job_id}>
          <select className="form-select" value={f.job_id} onChange={e => set('job_id', e.target.value)}
            style={errors.job_id ? { borderColor: '#A32D2D' } : undefined}>
            <option value="">{office ? 'Office - Roselle (no job)' : 'Pick the job...'}</option>
            {options.jobs.map(j => <option key={j.id} value={j.id}>{j.num} {j.name}</option>)}
          </select>
        </Field>
        {!office && (
          <Field label="Union local (optional)" error={errors.union_local}
            hint={!f.union_local ? 'Left blank, the new hire picks it. They can also correct it on their form.' : null}>
            <select className="form-select" value={f.union_local}
              onChange={e => { const v = e.target.value; setF(prev => ({ ...prev, union_local: v, trade_no: '' })); setErrors(prev => ({ ...prev, union_local: undefined, trade_no: undefined })) }}
              style={errors.union_local ? { borderColor: '#A32D2D' } : undefined}>
              <option value="">Let the new hire pick</option>
              {options.union_locals.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </Field>
        )}
        {!office && (options.union_trades || []).length > 0 && (
          <Field label="Trade (optional)" error={errors.trade_no}
            hint={f.union_local && tradeOptions.length && !f.trade_no ? 'Left blank, the new hire picks it from this union\'s list.' : null}>
            <select className="form-select" value={f.trade_no} onChange={e => set('trade_no', e.target.value)}
              disabled={!f.union_local || tradeOptions.length === 0}
              style={errors.trade_no ? { borderColor: '#A32D2D' } : undefined}>
              <option value="">{!f.union_local ? 'Pick the union first' : tradeOptions.length ? 'Let the new hire pick' : 'No trades for this union'}</option>
              {tradeOptions.map(t => <option key={t.trade_no} value={t.trade_no}>{t.name}</option>)}
            </select>
          </Field>
        )}
      </div>

      <InviteChannels smsAvailable={smsOn} channels={channels} setChannels={setChannels} language={language} setLanguage={setLanguage}
        consent={consent} setConsent={setConsent} error={errors.channels} />

      {msg && <div style={{ fontSize: 13, color: '#A32D2D', margin: '4px 0 10px' }}>{msg}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn-primary" disabled={busy || (channels.sms && !consent)} onClick={send} style={isMobile ? { flex: 1, padding: '10px 0' } : undefined}>
          {busy ? 'Sending...'
            : channels.email && !channels.sms ? 'Send link by email'
            : channels.email && channels.sms ? 'Send link by text and email'
            : 'Send link by text'}
        </button>
        {onCancel && <button className="btn" disabled={busy} onClick={onCancel}>Cancel</button>}
      </div>
    </div>
  )
}

// Text / Email checkboxes, English / Español, and the consent box for a text.
// `smsAvailable` comes from the server (options.sms_available): new-hire texts may only go
// from their own A2P campaign's number, so until TWILIO_NEWHIRE_FROM_NUMBER is set the
// Text option is off and the link goes by email.
// `smsOffMsg` turns the Text option off for another reason (the 3-texts-per-new-hire cap).
function InviteChannels({ channels, setChannels, language, setLanguage, consent, setConsent, error, smsAvailable = true, smsOffMsg = null }) {
  const smsOff = !smsAvailable ? "Texting new hires isn't set up yet - send by email." : smsOffMsg
  return (
    <div style={{ margin: '4px 0 12px', padding: 10, border: '0.5px solid var(--border2)', borderRadius: 8 }}>
      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'center', fontSize: 14 }}>
        <span style={{ fontWeight: 600, fontSize: 13 }}>Send by</span>
        {[['sms', 'Text'], ['email', 'Email']].map(([k, l]) => {
          const off = k === 'sms' && !!smsOff
          return (
            <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: off ? 'not-allowed' : 'pointer', opacity: off ? 0.5 : 1 }}>
              <input type="checkbox" checked={!off && !!channels[k]} disabled={off} onChange={e => setChannels(prev => ({ ...prev, [k]: e.target.checked }))} style={{ width: 18, height: 18 }} />{l}
            </label>
          )
        })}
        <span style={{ fontWeight: 600, fontSize: 13, marginLeft: 8 }}>Language</span>
        {[['en', 'English'], ['es', 'Español']].map(([k, l]) => (
          <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input type="radio" name="invite-lang" checked={language === k} onChange={() => setLanguage(k)} style={{ width: 18, height: 18 }} />{l}
          </label>
        ))}
      </div>
      {smsOff && <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 6 }}>{smsOff}</div>}
      {error && <div style={{ fontSize: 12, color: '#A32D2D', marginTop: 6 }}>{error}</div>}
      {!smsOff && channels.sms && (
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 10, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} style={{ width: 18, height: 18, marginTop: 1 }} />
          <span>The new hire agreed to get this text. <span style={{ color: 'var(--text3)' }}>(Required to send by text. It is a one-time text with the link - it does not sign them up for crew texts.)</span></span>
        </label>
      )}
    </div>
  )
}

// ── An invite still waiting on the new hire (office list) ────────────────────
function InvitedCard({ intake, onChanged, unionTrades, smsAvailable, smsMax = 3, smsLimitMsg }) {
  const textsSent = Number(intake.invite_sms_count) || 0
  const atCap = textsSent >= smsMax
  const smsOffMsg = atCap ? (smsLimitMsg || `This new hire has already been texted ${smsMax} times - send it by email or fill it out together on this device`) : null
  const unionHasTrades = ((unionTrades || []).find(g => g.union === intake.union_local) || { trades: [] }).trades.length > 0
  const [err, setErr] = useState('')
  const [ok, setOk] = useState([])
  const [busy, setBusy] = useState(false)
  const [resending, setResending] = useState(false)
  const [channels, setChannels] = useState(() => {
    const was = intake.invite_channels || []
    return smsAvailable && !atCap ? { sms: was.includes('sms'), email: was.includes('email') } : { sms: false, email: true }
  })
  const [language, setLanguage] = useState(intake.invite_language || 'en')
  const [consent, setConsent] = useState(false)
  const [cell, setCell] = useState(intake.cell_phone || '')
  const [email, setEmail] = useState(intake.email || '')
  const expired = intake.invite_expires_at && Date.parse(intake.invite_expires_at) < Date.now()
  const chText = (intake.invite_channels || []).map(c => c === 'sms' ? 'text' : 'email').join(' + ')

  const resend = async () => {
    setBusy(true); setErr(''); setOk([])
    const ch = Object.keys(channels).filter(k => channels[k])
    const r = await newHiresPost('resend-invite', { id: intake.id, channels: ch, language, sms_consent: channels.sms ? consent : false, cell_phone: cell, email })
    setBusy(false)
    if (r.error) { setErr((r.errors && Object.values(r.errors).filter(Boolean)[0]) || r.detail || r.error); return }
    const lines = deliveryLines(r, { cell_phone: cell, email })
    setOk(lines); setResending(false)
    if (r.intake) onChanged(r.intake)
  }
  const voidIt = async () => {
    const reason = window.prompt(`Void the invite for ${nameOf(intake)}? The link stops working. Say why:`)
    if (!reason || !reason.trim()) return
    setBusy(true)
    const r = await newHiresPost('void', { id: intake.id, reason })
    setBusy(false)
    if (r.error) { setErr(r.detail || r.error); return }
    onChanged(r.intake)
  }

  return (
    <div className="card" style={{ padding: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
        <span style={{ fontWeight: 600, fontSize: 15 }}>{nameOf(intake)}</span>
        <Chip status="invited" />
        <span style={{ fontSize: 11, color: 'var(--text3)', marginLeft: 'auto' }}>
          sent by {intake.submitted_by_name || intake.invite_sent_by_email} · {fmtWhen(intake.invite_sent_at)} · {chText}{intake.invite_language === 'es' ? ' · Spanish' : ''}
        </span>
      </div>
      <div style={{ fontSize: 13, padding: '2px 0' }}>
        Starts {fmtDate(intake.start_date)} - {startsAt(intake)}
        {intake.cell_phone ? ` · ${fmtPhone(intake.cell_phone)}` : ''}{intake.email ? ` · ${intake.email}` : ''}
      </div>
      {intake.employee_type !== 'office' && (
        <div style={{ fontSize: 13, padding: '2px 0' }}>
          {intake.union_local
            ? <>{intake.union_local}{intake.trade ? ` · Trade: ${intake.trade}` : unionHasTrades ? ' · Trade: new hire will pick' : ''}</>
            : 'Union: new hire will pick (and the trade)'}
        </div>
      )}
      <div style={{ fontSize: 13, padding: '2px 0', color: expired ? '#A32D2D' : 'var(--text2)', fontWeight: expired ? 600 : 400 }}>
        {expired ? `Link EXPIRED ${fmtWhen(intake.invite_expires_at)} - resend it` : `Link works until ${fmtWhen(intake.invite_expires_at)}`}
        <span style={{ marginLeft: 8, color: atCap ? '#854F0B' : 'var(--text3)', fontWeight: atCap ? 600 : 400 }}>· Texts sent: {textsSent} of {smsMax}</span>
      </div>
      {!resending && (
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <button className="btn btn-sm btn-primary" disabled={busy} onClick={() => { setResending(true); setOk([]) }}>Resend link</button>
          <button className="btn btn-sm" disabled={busy} onClick={voidIt}>Void</button>
        </div>
      )}
      {resending && (
        <div style={{ marginTop: 8 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 10px' }}>
            <Field label="Cell phone"><input className="form-input" type="tel" value={cell} onChange={e => setCell(e.target.value)} /></Field>
            <Field label="Email"><input className="form-input" type="email" value={email} onChange={e => setEmail(e.target.value)} /></Field>
          </div>
          <InviteChannels smsAvailable={!!smsAvailable} smsOffMsg={smsOffMsg} channels={channels} setChannels={setChannels} language={language} setLanguage={setLanguage} consent={consent} setConsent={setConsent} />
          <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 6 }}>A new link goes out and the old one stops working.</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-sm btn-primary" disabled={busy || (channels.sms && !consent)} onClick={resend}>{busy ? 'Sending...' : 'Send new link'}</button>
            <button className="btn btn-sm" disabled={busy} onClick={() => setResending(false)}>Cancel</button>
          </div>
        </div>
      )}
      {ok.map((l, i) => <div key={i} style={{ fontSize: 12, color: l.ok ? '#3B6D11' : '#A32D2D', marginTop: 4 }}>{l.text}</div>)}
      {err && <div style={{ fontSize: 12, color: '#A32D2D', marginTop: 6 }}>{err}</div>}
    </div>
  )
}

// After a send: what went where (and what didn't).
function InviteSentCard({ result, onAnother, isMobile }) {
  const anyOk = result.lines.some(l => l.ok)
  return (
    <div className="card" style={{ padding: 16 }}>
      <div style={{ fontWeight: 600, fontSize: 15, color: anyOk ? '#3B6D11' : '#A32D2D', marginBottom: 6 }}>
        {anyOk ? `Link sent to ${result.name}` : `The link did NOT reach ${result.name}`}
      </div>
      {result.lines.map((l, i) => <div key={i} style={{ fontSize: 13, color: l.ok ? 'var(--text2)' : '#A32D2D' }}>{l.text}</div>)}
      <div style={{ fontSize: 12, color: 'var(--text3)', margin: '8px 0 12px' }}>
        {anyOk
          ? 'When they send the form back, Carla and the owners are emailed, the same as any new hire.'
          : 'The invite is saved - the office can resend it from New Hires, or you can enter the details yourself.'}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button className="btn btn-primary" onClick={onAnother} style={isMobile ? { flex: 1, padding: '10px 0' } : undefined}>Enter another new hire</button>
      </div>
    </div>
  )
}

// Where the packet goes: always a text to the cell, plus email when there is one.
// `manual` reads "to ..." for the by-hand instructions; otherwise "text it to ...".
function packetTo(i, manual = false) {
  const cell = fmtPhone(i.cell_phone)
  if (manual) return i.email ? `to ${cell} by text and to ${i.email}` : `to ${cell} by text`
  return i.email ? `text it to ${cell} and email it to ${i.email}` : `text it to ${cell}`
}

// ── One intake, as the office sees it ────────────────────────────────────────
function OfficeCard({ intake, onChanged, onEdit, isMobile, docusignOn, canViewIds, today, onFlash }) {
  const [notes, setNotes] = useState(intake.office_notes || '')
  const [envId, setEnvId] = useState('')
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)
  const [summary, setSummary] = useState('')
  const [copied, setCopied] = useState(false)

  const act = async (action, body) => {
    setBusy(true); setErr('')
    const r = await newHiresPost(action, { id: intake.id, ...body })
    setBusy(false)
    if (r.error) { setErr((r.errors && Object.values(r.errors)[0]) || r.detail || r.error); return null }
    if (r.summary) setSummary(r.summary)
    if (r.docusign && !r.docusign.sent) {
      setErr(`Approved, but the DocuSign packet did not send: ${r.docusign.detail || r.docusign.error}. Fix it and press Send DocuSign packet.`)
    }
    // The card remounts when its status changes, so the success line goes to the page.
    if (r.docusign && r.docusign.sent && onFlash) {
      onFlash(`DocuSign packet sent to ${nameOf(intake)}${r.docusign.delivered_to ? ' by ' + r.docusign.delivered_to : ''}.`)
    }
    if (r.problems && r.problems.length) setErr('Linked, but some certifications did not move: ' + r.problems.join('; '))
    onChanged(r.intake)
    return r
  }
  const voidIt = async () => {
    const reason = window.prompt(`Void the intake for ${nameOf(intake)}? Say why (e.g. "didn't show", "duplicate"):`)
    if (reason && reason.trim()) act('void', { reason })
  }
  const summaryText = summary || [
    `New hire: ${nameOf(intake)}`,
    `Address: ${[intake.street, intake.apt ? 'Apt ' + intake.apt : ''].filter(Boolean).join(', ')}, ${intake.city}, ${intake.state} ${intake.zip}`,
    `Cell: ${fmtPhone(intake.cell_phone)}   Email: ${intake.email || 'none'}`,
    `Emergency contact: ${intake.emergency_name} ${fmtPhone(intake.emergency_phone)}`,
    `Start: ${fmtDate(intake.start_date)} at ${startsAt(intake)}`,
    ...(intake.employee_type === 'office'
      ? ['Type: Office employee (non-union)']
      : [`Union local: ${intake.union_local}`, `Trade: ${intake.trade || 'none'}`]),
  ].join('\n')
  const copy = async () => {
    try { await navigator.clipboard.writeText(summaryText); setCopied(true); setTimeout(() => setCopied(false), 2000) } catch { /* clipboard blocked */ }
  }

  const line = (k, v) => (
    <div style={{ display: 'flex', gap: 8, fontSize: 13, padding: '2px 0' }}>
      <span style={{ width: 120, flexShrink: 0, color: 'var(--text3)' }}>{k}</span><span style={{ flex: 1, minWidth: 0 }}>{v}</span>
    </div>
  )

  return (
    <div className="card" style={{ padding: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
        <span style={{ fontWeight: 600, fontSize: 15 }}>{nameOf(intake)}</span>
        <Chip status={intake.status} />
        {intake.employee_type === 'office' && (
          <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: '#E6F1FB', color: '#185FA5', fontWeight: 600 }}>Office</span>
        )}
        {intake.completed_by === 'new_hire' && (
          <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: '#EEEDFE', color: '#534AB7', fontWeight: 600 }}>
            Filled out by new hire{intake.language === 'es' ? ' (Spanish)' : ''}
          </span>
        )}
        <span style={{ fontSize: 11, color: 'var(--text3)', marginLeft: 'auto' }}>
          by {intake.submitted_by_name || intake.submitted_by_email} · {fmtWhen(intake.created_at)}
          {intake.payroll_notified_at ? ' · payroll emailed' : ''}
        </span>
      </div>
      {line('Starts', `${fmtDate(intake.start_date)} - ${startsAt(intake)}`)}
      {intake.employee_type === 'office'
        ? line('Type', 'Office employee (non-union)')
        : <>{line('Union local', <span>
          {intake.union_local}
          {intake.union_picked_by_new_hire && (
            <span style={{ marginLeft: 6, fontSize: 11, padding: '1px 7px', borderRadius: 10, background: '#FAEEDA', color: '#854F0B', fontWeight: 600 }}>
              {intake.invite_union_local ? `Union changed by new hire (was ${intake.invite_union_local})` : 'Union picked by new hire'}
            </span>
          )}
        </span>)}{line('Trade', <span>
          {intake.trade || 'none'}
          {intake.trade && intake.trade_picked_by_new_hire && (
            <span style={{ marginLeft: 6, fontSize: 11, padding: '1px 7px', borderRadius: 10, background: '#EEEDFE', color: '#534AB7', fontWeight: 600 }}>picked by new hire</span>
          )}
        </span>)}</>}
      {line('Address', `${[intake.street, intake.apt ? 'Apt ' + intake.apt : ''].filter(Boolean).join(', ')}, ${intake.city}, ${intake.state} ${intake.zip}`)}
      {line('Cell / email', intake.email ? `${fmtPhone(intake.cell_phone)} · ${intake.email}` : `${fmtPhone(intake.cell_phone)} · no email (packet goes by text)`)}
      {line('Emergency', `${intake.emergency_name} ${fmtPhone(intake.emergency_phone)}`)}
      {line('Date of birth', <DobLine dob={intake.date_of_birth} today={today} />)}
      {line('ID photos', <IdDocsLine intake={intake} canViewIds={canViewIds} busy={busy} onErr={setErr}
        onDone={() => {
          const n = intake.id_docs_count || 0
          if (window.confirm(n ? `Mark the I-9 done for ${nameOf(intake)}? This DELETES the ${n} ID photo${n === 1 ? '' : 's'} - save any copies you need for the I-9 file first.` : `Mark the I-9 done for ${nameOf(intake)}?`)) act('i9-done', {})
        }} />)}
      {(intake.certs || []).length > 0 && line('Certifications', <CertsLine intake={intake} onErr={setErr} />)}
      {intake.status !== 'void' && line('Employee #', <LinkEmployee intake={intake} busy={busy} onLink={(empId) => act('link-employee', { employee_id: empId })} />)}
      {intake.office_notes && intake.status !== 'submitted' && line('Office notes', intake.office_notes)}
      {intake.status === 'sent' && line('DocuSign', `${fmtWhen(intake.sent_at)}${intake.docusign_envelope_id ? ' · ' + intake.docusign_envelope_id : ''}`)}
      {intake.status === 'sent' && intake.docusign_envelope_id && line('Packet', <PacketLine intake={intake} canViewIds={canViewIds} busy={busy}
        onCheck={async () => {
          const r = await act('packet-status', { intake_id: intake.id })
          if (r && onFlash) onFlash(r.packet_status === 'completed' ? `${nameOf(intake)} has signed the packet.` : `DocuSign says: ${packetLabel(r.intake || intake)}.`)
        }} />)}
      {intake.status === 'void' && line('Voided', `${intake.void_reason || ''} (${intake.voided_by_email || ''})`)}

      {intake.status === 'submitted' && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 10 }}>
          <div style={{ flex: 1, minWidth: 180 }}>
            <label className="form-label">Office notes (optional)</label>
            <input className="form-input" value={notes} onChange={e => setNotes(e.target.value)} />
          </div>
          <button className="btn btn-success" disabled={busy} onClick={() => act('approve', { office_notes: notes })}>
            {busy ? 'Working...' : docusignOn ? 'Approve & send DocuSign' : 'Approve'}
          </button>
          <button className="btn" disabled={busy} onClick={() => onEdit(intake)}>Edit</button>
          <button className="btn" disabled={busy} onClick={voidIt}>Void</button>
        </div>
      )}

      {intake.status === 'approved' && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 6 }}>
            {docusignOn
              ? <>The DocuSign packet has not gone out yet. Press <b>Send DocuSign packet</b> to {packetTo(intake)}.</>
              : <>Next: send the DocuSign packet ("Cornerstone New Hire Packet" template) {packetTo(intake, true)} using the details below, then mark it sent.</>}
          </div>
          {docusignOn && (
            <div style={{ marginBottom: 8 }}>
              <button className="btn btn-primary" disabled={busy} onClick={() => act('send-docusign', {})}>
                {busy ? 'Sending...' : 'Send DocuSign packet'}
              </button>
            </div>
          )}
          <pre style={{ fontSize: 12, background: 'var(--surface2)', padding: 10, borderRadius: 8, whiteSpace: 'pre-wrap', margin: 0 }}>{summaryText}</pre>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 8 }}>
            <button className="btn btn-sm" onClick={copy}>{copied ? 'Copied' : 'Copy details'}</button>
            <div style={{ flex: 1, minWidth: isMobile ? '100%' : 220 }}>
              <label className="form-label">DocuSign envelope ID (optional)</label>
              <input className="form-input" value={envId} onChange={e => setEnvId(e.target.value)} placeholder="from the sent envelope" />
            </div>
            <button className={docusignOn ? 'btn' : 'btn btn-primary'} disabled={busy} onClick={() => act('mark-sent', { envelope_id: envId })}>
              {docusignOn ? 'Sent it by hand - mark sent' : 'Mark DocuSign sent'}
            </button>
            <button className="btn" disabled={busy} onClick={() => onEdit(intake)}>Edit</button>
            <button className="btn" disabled={busy} onClick={voidIt}>Void</button>
          </div>
        </div>
      )}
      {err && <div style={{ fontSize: 12, color: '#A32D2D', marginTop: 6 }}>{err}</div>}
    </div>
  )
}

// ── DocuSign packet status (2026-10-31) ──────────────────────────────────────
function packetLabel(i) {
  switch (i.packet_status) {
    case 'delivered': return 'Opened by new hire'
    case 'completed': return `SIGNED ${i.packet_signed_at ? fmtWhen(i.packet_signed_at) : ''}`.trim()
    case 'declined': return `Declined${i.packet_note ? ' - ' + i.packet_note : ''}`
    case 'voided': return 'Voided'
    default: return 'Packet sent'
  }
}
const PACKET_TONE = {
  completed: { background: '#E1F5EE', color: '#0F6E56' },
  declined: { background: '#FCEBEB', color: '#A32D2D' },
  voided: { background: 'var(--surface2)', color: 'var(--text2)' },
}
function PacketLine({ intake, canViewIds, busy, onCheck }) {
  const signed = intake.packet_status === 'completed'
  const tone = PACKET_TONE[intake.packet_status] || { background: '#E6F1FB', color: '#185FA5' }
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, fontWeight: 600, ...tone }}>{packetLabel(intake)}</span>
        {!signed && <button className="btn btn-sm" disabled={busy} onClick={onCheck}>{busy ? 'Checking...' : 'Check status'}</button>}
      </div>
      {signed && canViewIds && <SignedDocs intake={intake} />}
    </div>
  )
}
function SignedDocs({ intake }) {
  const [docs, setDocs] = useState(null)
  const [err, setErr] = useState('')
  const [getting, setGetting] = useState('')
  useEffect(() => {
    let live = true
    newHiresGet('packet-docs', { intake_id: intake.id }).then(r => {
      if (!live) return
      if (r.error) setErr(r.detail || r.error); else setDocs(r.documents || [])
    })
    return () => { live = false }
  }, [intake.id])
  const get = async (d) => {
    setGetting(d.document_id); setErr('')
    const r = await downloadNewHirePacketDoc(intake.id, d.document_id)
    setGetting('')
    if (r.error) setErr(r.detail || r.error)
  }
  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 4 }}>Signed documents (owners and Carla only)</div>
      {!docs && !err && <div style={{ fontSize: 12, color: 'var(--text3)' }}>Loading...</div>}
      {docs && docs.length === 0 && <div style={{ fontSize: 12, color: 'var(--text3)' }}>No documents found in DocuSign.</div>}
      {docs && docs.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {docs.map(d => (
            <button key={d.document_id} className="btn btn-sm" disabled={!!getting} onClick={() => get(d)}>
              {getting === d.document_id ? 'Downloading...' : `⬇ ${d.name}`}
            </button>
          ))}
        </div>
      )}
      {err && <div style={{ fontSize: 12, color: '#A32D2D', marginTop: 4 }}>{err}</div>}
    </div>
  )
}

function DobLine({ dob, today }) {
  if (!dob) return <span style={{ color: 'var(--text3)' }}>not entered</span>
  const age = ageOn(dob, today)
  return (
    <span>
      {fmtDate(dob)}{age != null ? ` (age ${age})` : ''}
      {age != null && age < 18 && <span style={{ marginLeft: 6, fontSize: 11, padding: '1px 7px', borderRadius: 10, background: '#FAEEDA', color: '#854F0B', fontWeight: 600 }}>Under 18</span>}
    </span>
  )
}

function IdDocsLine({ intake, canViewIds, busy, onErr, onDone }) {
  const n = intake.id_docs_count || 0
  const whenBy = (at, by) => `${fmtWhen(at)}${by ? ' by ' + by : ''}`
  if (intake.id_docs_deleted_at) {
    const why = intake.id_docs_deleted_reason === 'void' ? 'when the intake was voided' : 'after the I-9 was done'
    return <span style={{ color: 'var(--text3)' }}>{n} deleted {why} ({whenBy(intake.id_docs_deleted_at, intake.id_docs_deleted_by)})</span>
  }
  const i9 = intake.i9_done_at ? <span style={{ color: '#3B6D11' }}>I-9 done {whenBy(intake.i9_done_at, intake.i9_done_by_email)}</span> : null
  if (!canViewIds) return <span>{n ? `${n} uploaded (owners and Carla only)` : 'none uploaded'}{i9 ? <> · {i9}</> : null}</span>
  return (
    <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
      {n === 0 && <span style={{ color: 'var(--text3)' }}>none uploaded</span>}
      {(intake.id_docs || []).map(d => d.has_file && (
        <button key={d.n} className="btn btn-sm" onClick={() => openPhoto(intake.id, 'id', d.n, onErr)}>View {d.n + 1}</button>
      ))}
      {i9}
      {!intake.i9_done_at && intake.status !== 'void' && (
        <button className="btn btn-sm" disabled={busy} onClick={onDone}>{n ? 'I-9 done - delete ID photos' : 'Mark I-9 done'}</button>
      )}
    </span>
  )
}

function CertsLine({ intake, onErr }) {
  return (
    <span style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {(intake.certs || []).map(c => (
        <span key={c.n} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span>{c.cert_type_name}</span>
          {c.employee_cert_id
            ? <span style={{ fontSize: 12, color: '#3B6D11' }}>on his record (Certifications - needs review)</span>
            : c.has_file
              ? <button className="btn btn-sm" onClick={() => openPhoto(intake.id, 'cert', c.n, onErr)}>View photo</button>
              : <span style={{ fontSize: 12, color: 'var(--text3)' }}>no photo</span>}
        </span>
      ))}
    </span>
  )
}

// Link the intake to his Foundation employee record (once Carla has added him).
// That is what moves the certification photos onto his record for review.
let employeesCache = null
function LinkEmployee({ intake, busy, onLink }) {
  const [open, setOpen] = useState(false)
  const [list, setList] = useState(employeesCache)
  const [q, setQ] = useState(intake.last_name || '')
  const [loadErr, setLoadErr] = useState('')
  useEffect(() => {
    if (!open || list) return
    newHiresGet('employees').then(r => {
      if (r.error) { setLoadErr(r.detail || r.error); return }
      employeesCache = r.employees || []
      setList(employeesCache)
    })
  }, [open, list])
  if (intake.linked_employee_id) {
    return <span>#{intake.linked_employee_id} <span style={{ fontSize: 12, color: 'var(--text3)' }}>linked {fmtWhen(intake.linked_at)}</span></span>
  }
  if (!open) {
    return (
      <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ color: 'var(--text3)' }}>not linked yet</span>
        <button className="btn btn-sm" onClick={() => setOpen(true)}>Link to employee</button>
      </span>
    )
  }
  const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean)
  const hits = (list || []).filter(e => words.every(w => String(e.name || '').toLowerCase().includes(w) || String(e.id) === w)).slice(0, 8)
  return (
    <span style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
      <span style={{ fontSize: 12, color: 'var(--text3)' }}>Once he is in Foundation (and the sync has run), pick his record. His certification photos move onto it for review.</span>
      <input className="form-input" value={q} onChange={e => setQ(e.target.value)} placeholder="Search name or employee #" style={{ maxWidth: 320 }} />
      {loadErr && <span style={{ fontSize: 12, color: '#A32D2D' }}>{loadErr}</span>}
      {!list && !loadErr && <span style={{ fontSize: 12, color: 'var(--text3)' }}>Loading...</span>}
      {list && hits.length === 0 && <span style={{ fontSize: 12, color: 'var(--text3)' }}>No match - he may not be in Foundation yet.</span>}
      {hits.map(e => (
        <span key={e.id} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span>{e.name} <span style={{ color: 'var(--text3)' }}>#{e.id}{e.active === false ? ' · inactive' : ''}</span></span>
          <button className="btn btn-sm" disabled={busy} onClick={() => { if (window.confirm(`Link ${nameOf(intake)} to ${e.name} #${e.id}?`)) onLink(e.id) }}>Link</button>
        </span>
      ))}
      <span><button className="btn btn-sm" onClick={() => setOpen(false)}>Cancel</button></span>
    </span>
  )
}

// ── DocuSign connection strip (office only; hidden when DocuSign isn't set up) ─
function DocusignStatus({ ds, onCheck }) {
  if (!ds) return null
  const box = (bg, fg) => ({ fontSize: 13, background: bg, color: fg, padding: '8px 12px', borderRadius: 8, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' })
  if (ds.checking) return <div style={box('var(--surface2)', 'var(--text3)')}>Checking the DocuSign connection...</div>
  if (ds.connected) return <div style={box('#EAF3DE', '#3B6D11')}>DocuSign connected{ds.demo ? ' (developer / demo account - test envelopes only)' : ''}. Approving sends the packet.</div>
  // The consent link is owner-only (the server sends it to owners alone).
  if (ds.error === 'consent_required' && ds.consent_owner_only) {
    return (
      <div style={box('#FAEEDA', '#854F0B')}>
        <span style={{ flex: 1, minWidth: 200 }}>DocuSign needs reconnecting - ask an owner to reconnect it.</span>
        <button className="btn btn-sm" onClick={onCheck}>Check again</button>
      </div>
    )
  }
  if (ds.error === 'consent_required' && ds.consent_url) {
    return (
      <div style={box('#FAEEDA', '#854F0B')}>
        <span style={{ flex: 1, minWidth: 200 }}>DocuSign needs a one-time OK before Crew Dispatch can send packets.</span>
        <a className="btn btn-primary btn-sm" href={ds.consent_url} target="_blank" rel="noreferrer">Connect DocuSign</a>
        <button className="btn btn-sm" onClick={onCheck}>Check again</button>
        <span style={{ flexBasis: '100%', fontSize: 12 }}>Sign in to DocuSign as the Crew Dispatch sender user, not your own account.</span>
      </div>
    )
  }
  return (
    <div style={box('#FCEBEB', '#A32D2D')}>
      <span style={{ flex: 1, minWidth: 200 }}>DocuSign is not working: {ds.detail || ds.error}{ds.error === 'consent_required' ? ' (no redirect address - set DOCUSIGN_REDIRECT_URI)' : ''}</span>
      <button className="btn btn-sm" onClick={onCheck}>Check again</button>
    </div>
  )
}

// ── How to start a new hire: two big cards (2026-10-25) ──────────────────────
// Foremen on iPads couldn't tell "fill it out here" from "send it to their phone" by the
// old button labels. Wording is "this device" everywhere (the app can't reliably tell an
// iPad from another tablet). The grid stacks the cards on a narrow screen by itself.
const START = {
  invite: { icon: '📱', title: "Send to the new hire's device", sub: 'We text or email them a link. They fill it out on their own phone, tablet or computer, in English or Spanish.' },
  enter: { icon: '📝', title: 'Fill it out here, on this device', sub: 'Hand this device to the new hire, or fill it in together right now.' },
}
function StartChoice({ onPick }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
      {['invite', 'enter'].map(k => (
        <button key={k} type="button" className="card" onClick={() => onPick(k)}
          style={{ minHeight: 128, padding: 16, textAlign: 'left', cursor: 'pointer', border: '1.5px solid var(--border2)', borderRadius: 12, display: 'flex', gap: 14, alignItems: 'flex-start', background: 'var(--surface, #fff)', font: 'inherit', color: 'inherit' }}>
          <span style={{ fontSize: 34, lineHeight: 1 }} aria-hidden="true">{START[k].icon}</span>
          <span style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontWeight: 600, fontSize: 16 }}>{START[k].title}</span>
            <span style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.4 }}>{START[k].sub}</span>
          </span>
        </button>
      ))}
    </div>
  )
}

// The reminder at the top of each start screen, with a way back to the two cards.
function StartNote({ kind, onChange }) {
  const text = kind === 'invite'
    ? "📱 The new hire will fill this out on their own device. You only enter who they are and where they're starting."
    : "📝 You're filling this out here, on this device."
  return (
    <div style={{ fontSize: 13, background: 'var(--surface2)', padding: '8px 12px', borderRadius: 8, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
      <span style={{ flex: 1, minWidth: 200 }}>{text}</span>
      <button type="button" className="btn btn-sm" onClick={onChange}>Change</button>
    </div>
  )
}

// ── Screen ───────────────────────────────────────────────────────────────────
export default function NewHireView({ isMobile }) {
  const [options, setOptions] = useState(null)
  const [intakes, setIntakes] = useState([])
  const [loadErr, setLoadErr] = useState('')
  const [mode, setMode] = useState('list')      // 'list' | 'new' | 'edit'
  const [editing, setEditing] = useState(null)
  const [flash, setFlash] = useState('')
  const [showDone, setShowDone] = useState(false)

  const [sentName, setSentName] = useState('')   // foreman confirmation
  const [ds, setDs] = useState(null)              // DocuSign connection status (office)
  const [formKey, setFormKey] = useState(0)
  const [how, setHow] = useState(null)             // foreman: null (the two cards) | 'enter' | 'invite'
  const [inviteResult, setInviteResult] = useState(null)

  const load = async () => {
    const o = await newHiresGet('options')
    if (o.error) { setLoadErr(o.detail || o.error); return }
    // Only the office gets the list — the server refuses it to anyone else.
    if (o.can_review) {
      const l = await newHiresGet('list')
      if (l.error) { setLoadErr(l.detail || l.error); return }
      setIntakes(l.intakes || [])
    }
    setOptions(o); setLoadErr('')
    if (o.can_review && o.docusign_configured) checkDocusign()
  }
  const checkDocusign = async () => {
    setDs({ checking: true })
    const r = await newHiresGet('docusign-status', { origin: window.location.origin })
    // A 200 status reply carries its own error field (e.g. consent_required) next to
    // the consent link, so key on ok, not on error.
    setDs(r.ok ? r : { error: r.error, detail: r.detail })
  }
  useEffect(() => { load() }, [])

  const reviewer = !!(options && options.can_review)
  const groups = useMemo(() => ({
    invited: intakes.filter(i => i.status === 'invited'),
    submitted: intakes.filter(i => i.status === 'submitted'),
    approved: intakes.filter(i => i.status === 'approved'),
    done: intakes.filter(i => i.status === 'sent' || i.status === 'void'),
  }), [intakes])

  const upsert = (row) => setIntakes(prev => {
    const i = prev.findIndex(x => x.id === row.id)
    if (i === -1) return [row, ...prev]
    const next = [...prev]; next[i] = row; return next
  })

  if (loadErr) return <div style={{ padding: 16, color: '#A32D2D' }}>Couldn't load new hires: {loadErr}</div>
  if (!options) return <div style={{ padding: 16, color: 'var(--text3)' }}>Loading...</div>

  const pad = isMobile ? 10 : 14
  const wrap = { padding: pad, display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 820 }

  // Back to the two cards, with a fresh form next time.
  const restart = () => { setInviteResult(null); setSentName(''); setHow(null); setFormKey(k => k + 1) }

  // ── Foreman (anyone who isn't office): the cards, a form, then a confirmation. ─
  if (!reviewer) {
    if (inviteResult) {
      return (
        <div style={wrap}>
          <InviteSentCard result={inviteResult} isMobile={isMobile} onAnother={restart} />
        </div>
      )
    }
    if (!how && !sentName) {
      return <div style={wrap}><StartChoice onPick={setHow} /></div>
    }
    if (how === 'invite') {
      return (
        <div style={wrap}>
          <StartNote kind="invite" onChange={restart} />
          <SendInviteForm key={formKey} options={options} isMobile={isMobile} onSent={setInviteResult} />
        </div>
      )
    }
    if (sentName) {
      return (
        <div style={wrap}>
          <div className="card" style={{ padding: 16 }}>
            <div style={{ fontWeight: 600, fontSize: 15, color: '#3B6D11', marginBottom: 6 }}>Sent to the office</div>
            <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 12 }}>
              {sentName}'s details are with the office. They'll get the DocuSign paperwork by text once the office checks it.
            </div>
            <button className="btn btn-primary" onClick={restart}
              style={isMobile ? { width: '100%', padding: '10px 0' } : undefined}>Enter another new hire</button>
          </div>
        </div>
      )
    }
    return (
      <div style={wrap}>
        <StartNote kind="enter" onChange={restart} />
        <IntakeForm key={formKey} options={options} initial={null} isMobile={isMobile}
          onDone={(row) => setSentName(nameOf(row))} />
      </div>
    )
  }

  if (mode === 'invite') {
    return (
      <div style={wrap}>
        <StartNote kind="invite" onChange={() => setMode('list')} />
        <SendInviteForm options={options} isMobile={isMobile} onCancel={() => setMode('list')}
          onSent={(r) => {
            setMode('list')
            setFlash(r.lines.map(l => l.text).join('. ') + '.')
            load()
          }} />
      </div>
    )
  }

  if (mode !== 'list') {
    return (
      <div style={wrap}>
        {mode === 'new' && <StartNote kind="enter" onChange={() => setMode('list')} />}
        <IntakeForm options={options} initial={mode === 'edit' ? editing : null} isMobile={isMobile}
          onCancel={() => { setMode('list'); setEditing(null) }}
          onDone={(row) => {
            upsert(row); setMode('list'); setEditing(null)
            setFlash(mode === 'edit' ? 'Saved.' : `Saved. Carla and the owners have been emailed about ${nameOf(row)}.`)
          }} />
      </div>
    )
  }

  const startEdit = (i) => { setEditing(i); setMode('edit'); setFlash('') }

  return (
    <div style={wrap}>
      <div style={{ fontWeight: 600, fontSize: 16 }}>New hires</div>
      <StartChoice onPick={(k) => { setMode(k === 'invite' ? 'invite' : 'new'); setFlash('') }} />
      {flash && <div style={{ fontSize: 13, background: '#EAF3DE', color: '#3B6D11', padding: '8px 12px', borderRadius: 8 }}>{flash}</div>}
      <DocusignStatus ds={ds} onCheck={checkDocusign} />

        <>
          {groups.invited.length > 0 && <>
            <div className="section-lbl" style={{ borderRadius: 6 }}>Invited - waiting on new hire ({groups.invited.length})</div>
            {groups.invited.map(i => <InvitedCard key={i.id + i.status + (i.invite_sent_at || '')} intake={i} onChanged={upsert} unionTrades={options.union_trades} smsAvailable={!!options.sms_available} smsMax={options.invite_sms_max || 3} smsLimitMsg={options.sms_limit_msg} />)}
          </>}

          <div className="section-lbl" style={{ borderRadius: 6 }}>Waiting for office ({groups.submitted.length})</div>
          {groups.submitted.length === 0 && <div style={{ fontSize: 13, color: 'var(--text3)' }}>Nothing waiting.</div>}
          {groups.submitted.map(i => <OfficeCard key={i.id + i.status} intake={i} onChanged={upsert} onEdit={startEdit} isMobile={isMobile} docusignOn={!!options.docusign_configured} canViewIds={!!options.can_view_ids} today={options.today} onFlash={setFlash} />)}

          <div className="section-lbl" style={{ borderRadius: 6 }}>Approved - send DocuSign ({groups.approved.length})</div>
          {groups.approved.length === 0 && <div style={{ fontSize: 13, color: 'var(--text3)' }}>None.</div>}
          {groups.approved.map(i => <OfficeCard key={i.id + i.status} intake={i} onChanged={upsert} onEdit={startEdit} isMobile={isMobile} docusignOn={!!options.docusign_configured} canViewIds={!!options.can_view_ids} today={options.today} onFlash={setFlash} />)}

          <div className="section-lbl" style={{ borderRadius: 6, cursor: 'pointer' }} onClick={() => setShowDone(v => !v)}>
            {showDone ? '▾' : '▸'} Sent and void ({groups.done.length})
          </div>
          {showDone && groups.done.map(i => <OfficeCard key={i.id + i.status} intake={i} onChanged={upsert} onEdit={startEdit} isMobile={isMobile} docusignOn={!!options.docusign_configured} canViewIds={!!options.can_view_ids} today={options.today} onFlash={setFlash} />)}
        </>
    </div>
  )
}
